import React, { useState, useEffect } from 'react';
import { apiRequest } from '../../services/api';

/**
 * AIInsightsGenerator - Component for generating and displaying AI insights
 * Allows teachers to generate insights for students with different timeframes
 */
const AIInsightsGenerator = ({ studentId, studentName }) => {
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [timeframe, setTimeframe] = useState('week');
  const [error, setError] = useState(null);

  // Fetch existing insights
  useEffect(() => {
    fetchInsights();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId]);

  const fetchInsights = async () => {
    try {
      setLoading(true);
      const data = await apiRequest(`/api/teacher/insights/student/${studentId}`, 'GET');
      setInsights(data.insights || []);
    } catch (err) {
      console.error('Failed to fetch insights:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const generateInsights = async () => {
    try {
      setGenerating(true);
      setError(null);
      const result = await apiRequest(
        `/api/teacher/insights/student/${studentId}/generate`,
        'POST',
        { timeframe }
      );

      if (result.success) {
        // Refresh insights list
        await fetchInsights();
        alert(`✓ Generated ${result.insights.length} new insight(s)!`);
      } else {
        setError(result.error || 'Failed to generate insights');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setGenerating(false);
    }
  };

  const markAsReviewed = async (insightId) => {
    try {
      await apiRequest(`/api/teacher/insights/${insightId}/review`, 'POST');
      // Update local state
      setInsights(insights.map(insight =>
        insight.id === insightId
          ? { ...insight, reviewed_by_teacher: true, reviewed_at: new Date().toISOString() }
          : insight
      ));
    } catch (err) {
      alert(`Failed to mark as reviewed: ${err.message}`);
    }
  };

  const getInsightIcon = (type) => {
    switch (type) {
      case 'struggle_area':
        return '🔴';
      case 'strength_area':
        return '🟢';
      case 'topic_trend':
        return '📊';
      case 'engagement_alert':
        return '⚠️';
      default:
        return '💡';
    }
  };

  const getInsightTypeLabel = (type) => {
    switch (type) {
      case 'struggle_area':
        return 'Struggling With';
      case 'strength_area':
        return 'Excelling In';
      case 'topic_trend':
        return 'Topic Trend';
      case 'engagement_alert':
        return 'Engagement Alert';
      default:
        return 'Insight';
    }
  };

  const getConfidenceBadge = (confidence) => {
    const percentage = Math.round(confidence * 100);
    let colorClass = 'bg-gray-100 text-gray-700';
    if (percentage >= 90) colorClass = 'bg-green-100 text-green-700';
    else if (percentage >= 75) colorClass = 'bg-blue-100 text-blue-700';
    else if (percentage >= 60) colorClass = 'bg-yellow-100 text-yellow-700';

    return (
      <span className={`text-xs px-2 py-1 rounded-full ${colorClass}`}>
        {percentage}% confidence
      </span>
    );
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          <span className="ml-3 text-gray-600">Loading insights...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      {/* Header with Generate Button */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-1">
            🤖 AI Learning Insights
          </h3>
          <p className="text-sm text-gray-600">
            AI-powered analysis of {studentName}'s learning patterns
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={timeframe}
            onChange={(e) => setTimeframe(e.target.value)}
            disabled={generating}
            className="text-sm px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-indigo-500"
          >
            <option value="day">Last Day</option>
            <option value="week">Last Week</option>
            <option value="month">Last Month</option>
          </select>
          <button
            onClick={generateInsights}
            disabled={generating}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 text-sm font-medium"
          >
            {generating ? (
              <>
                <span className="inline-block animate-spin mr-2">⟳</span>
                Generating...
              </>
            ) : (
              '✨ Generate New Insights'
            )}
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Insights List */}
      {insights.length === 0 ? (
        <div className="text-center py-8">
          <div className="text-4xl mb-2">🤖</div>
          <p className="text-gray-600 mb-2">No AI insights yet</p>
          <p className="text-sm text-gray-500">
            Click "Generate New Insights" to analyze this student's learning patterns
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {insights.map((insight) => (
            <div
              key={insight.id}
              className={`border rounded-lg p-4 ${
                insight.reviewed_by_teacher
                  ? 'border-gray-200 bg-gray-50'
                  : 'border-indigo-200 bg-indigo-50'
              }`}
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-start gap-2">
                  <span className="text-2xl">{getInsightIcon(insight.insight_type)}</span>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-semibold text-gray-500 uppercase">
                        {getInsightTypeLabel(insight.insight_type)}
                      </span>
                      {getConfidenceBadge(insight.confidence_score)}
                    </div>
                    <h4 className="font-semibold text-gray-900 mb-1">
                      {insight.topic}
                    </h4>
                  </div>
                </div>
                {!insight.reviewed_by_teacher && (
                  <button
                    onClick={() => markAsReviewed(insight.id)}
                    className="text-xs px-3 py-1 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700"
                  >
                    Mark as Reviewed
                  </button>
                )}
              </div>

              <p className="text-sm text-gray-700 mb-2">
                {insight.summary}
              </p>

              {insight.detailed_analysis && (
                <details className="text-sm text-gray-600">
                  <summary className="cursor-pointer hover:text-indigo-600 font-medium">
                    View detailed analysis →
                  </summary>
                  <p className="mt-2 pl-4 border-l-2 border-gray-300">
                    {insight.detailed_analysis}
                  </p>
                </details>
              )}

              <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
                <span>
                  Generated: {new Date(insight.created_at).toLocaleDateString()}
                </span>
                {insight.reviewed_by_teacher && (
                  <span className="text-green-600">
                    ✓ Reviewed {new Date(insight.reviewed_at).toLocaleDateString()}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AIInsightsGenerator;
